import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["saveButton", "resetButton", "statusIndicator"]
  static values = {
    originalDoc: Object,
    workingDoc: Object,
    claimsExperienceId: String,
    updateUrl: String,
    saveUrl: String
  }

  connect() {
    console.log("Claims experience editor controller connected")

    // Initialize working doc as copy of original if not already set
    if (!this.workingDocValue || Object.keys(this.workingDocValue).length === 0) {
      this.workingDocValue = this.deepClone(this.originalDocValue)
    }

    // Initialize UI state
    this.initialUpdateHasChanges()
    this.pendingRefresh = null

    // Setup debounced refresh
    this.refreshDebounced = this.debounce(this.refreshEditor.bind(this), 300)

    console.log("Initialized with docs:", {
      original: Object.keys(this.originalDocValue),
      working: Object.keys(this.workingDocValue)
    })
  }

  // Called by simple field editor controllers when values change
  fieldValueChanged(fieldPath, newValue) {
    console.log(`Field changed: ${fieldPath} = ${newValue}`)

    // Update working doc
    this.setFieldInWorkingDoc(fieldPath, newValue)

    // Check if any field has changes (for save button state)
    this.updateHasChanges()

    // Trigger refresh for calculated fields (Paid Loss Ratio)
    this.refreshDebounced()
  }

  setFieldInWorkingDoc(fieldPath, value) {
    const pathParts = fieldPath.split(".")
    let workingDoc = this.workingDocValue
    let current = workingDoc

    console.log(`Setting field ${fieldPath} to ${value}`)
    console.log(`Path parts:`, pathParts)

    // Navigate through the FieldDoc structure intelligently
    for (let i = 0; i < pathParts.length - 1; i++) {
      const currentKey = pathParts[i]

      // Check if current level has sections array (FieldDoc structure)
      if (current.sections && Array.isArray(current.sections)) {
        // Find section by name in sections array
        let foundSection = current.sections.find(section => section.name === currentKey)
        console.log("found section: ", foundSection)
        if (!foundSection) {
          // Create new section if not found
          foundSection = { name: currentKey, sections: [], fields: [] }
          current.sections.push(foundSection)
        }
        current = foundSection
      } else if (current.fields && Array.isArray(current.fields)) {
        // Look for field in fields array
        let foundField = current.fields.find(field => field.name === currentKey)
        console.log("found field: ", foundField)
        if (!foundField) {
          // If we're not at the final key, this should be a section
          if (i < pathParts.length - 2) {
            foundField = { name: currentKey, sections: [], fields: [] }
            current.fields.push(foundField)
          }
        }
        if (foundField) {
          current = foundField
        }
      } else {
        // Regular nested object navigation
        console.log("navigating to key: ", currentKey)
        current = current[currentKey]
      }
    }

    // Set the field value at the final location
    const fieldName = pathParts[pathParts.length - 1]

    if (current.fields && Array.isArray(current.fields)) {
      // We're in a FieldDoc section, look for field in fields array
      let foundField = current.fields.find(field => field.name === fieldName)
      console.log("found field: ", foundField)
      if (foundField) {
        foundField.value = `${value}`
      } else {
        // Create new field
        current.fields.push({ name: fieldName, value: value, history: [] })
      }
    } else if (current[fieldName] && typeof current[fieldName] === "object" && current[fieldName].value !== undefined) {
      // This is a FieldValue structure, update the value
      current[fieldName].value = value
    } else {
      // Simple value or new field
      current[fieldName] = { value: value }
    }

    console.log(`Updated working doc field ${fieldName}:`, current[fieldName] || current.fields?.find(f => f.name === fieldName))
    this.workingDocValue = workingDoc
    console.log("Current working doc state:", this.workingDocValue)
  }

  async refreshEditor() {
    if (this.pendingRefresh) {
      this.pendingRefresh.abort()
    }

    this.showRefreshing()

    try {
      this.pendingRefresh = fetch(this.updateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({
          working_doc: this.workingDocValue
        })
      })

      const response = await this.pendingRefresh

      if (response.ok) {
        const turboStreamContent = await response.text()
        Turbo.renderStreamMessage(turboStreamContent)
        this.showRefreshSuccess()
      } else {
        this.showRefreshError("Failed to refresh claims experience")
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Refresh failed:", error)
        this.showRefreshError("Network error occurred")
      }
    } finally {
      this.hideRefreshing()
      this.pendingRefresh = null
    }
  }

  async saveClaimsExperience() {
    if (this.saveButtonTarget.disabled) return
    this.saveButtonTarget.disabled = true
    this.showSaving()

    try {
      const response = await fetch(this.saveUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: JSON.stringify({
          working_doc: this.workingDocValue
        })
      })

      const result = await response.json()

      if (result.status === "success") {
        // Update original doc to match working doc
        this.originalDocValue = this.deepClone(this.workingDocValue)
        this.markAsSaved()
        this.showSaveSuccess()
      } else {
        this.showSaveError(result.errors || ["Save failed"])
      }
    } catch (error) {
      console.error("Save failed:", error)
      this.showSaveError(["Network error occurred"])
    } finally {
      this.saveButtonTarget.disabled = false
      this.hideSaving()
    }
  }

  resetClaimsExperience() {
    if (this.resetButtonTarget.disabled) return

    // Reset working doc to original
    this.workingDocValue = this.deepClone(this.originalDocValue)
    this.hasChanges = false
    this.updateSaveButtonState()

    // Trigger refresh to show original values
    this.refreshEditor()
  }

  initialUpdateHasChanges() {
    console.log("Checking for initial changes; has changes:", this.hasChanges)
    // Check if any simple field editor has changes
    const fieldEditors = this.element.querySelectorAll("[data-controller*='simple-field-editor']")
    let hasChanges = false

    fieldEditors.forEach(editor => {
      if (editor.dataset.simpleFieldEditorReadOnlyValue !== "true" && editor.dataset.simpleFieldEditorIsInitiallyChangedValue === "true") {
        console.log("got change in: ", editor)
        hasChanges = true
      }
    })

    console.log("Update has changes:", hasChanges)
    this.hasChanges = hasChanges
    this.updateSaveButtonState()
  }

  updateHasChanges() {
    console.log("Checking for changes; has changes:", this.hasChanges)
    // Check if any simple field editor has changes
    const fieldEditors = this.element.querySelectorAll("[data-controller*='simple-field-editor']")
    let hasChanges = false

    fieldEditors.forEach(editor => {
      const controller = this.application.getControllerForElementAndIdentifier(editor, "simple-field-editor")
      if (controller && controller.isFieldChanged()) {
        hasChanges = true
      }
    })

    console.log("Update has changes:", hasChanges)
    this.hasChanges = hasChanges
    this.updateSaveButtonState()
  }

  markAsChanged() {
    this.hasChanges = true
    this.updateSaveButtonState()
  }

  markAsSaved() {
    this.hasChanges = false
    this.updateSaveButtonState()
  }

  updateSaveButtonState() {
    if (this.hasSaveButtonTarget) {
      console.log("Updating save button state; has changes:", this.hasChanges)
      this.saveButtonTarget.disabled = !this.hasChanges

      if (this.hasChanges) {
        this.saveButtonTarget.classList.remove("button__disabled")
        this.resetButtonTarget.classList.remove("button__disabled")
        this.saveButtonTarget.disabled = false
        this.resetButtonTarget.disabled = false
      } else {
        this.saveButtonTarget.classList.add("button__disabled")
        this.resetButtonTarget.classList.add("button__disabled")
        this.saveButtonTarget.disabled = true
        this.resetButtonTarget.disabled = true
      }
    }
  }

  // UI State Methods
  showRefreshing() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = "Updating..."
      this.statusIndicatorTarget.className = "text-sm text-blue-600"
    }
  }

  hideRefreshing() {
    // Status will be updated by success/error methods
  }

  showRefreshSuccess() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = "Updated"
      this.statusIndicatorTarget.className = "text-sm text-green-600"
      setTimeout(() => {
        if (this.hasStatusIndicatorTarget) {
          this.statusIndicatorTarget.textContent = ""
        }
      }, 2000)
    }
  }

  showRefreshError(message) {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = `Update error: ${message}`
      this.statusIndicatorTarget.className = "text-sm text-red-600"
    }
  }

  showSaving() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = "Saving..."
      this.statusIndicatorTarget.className = "text-sm text-blue-600"
    }
  }

  hideSaving() {
    // Status will be updated by success/error methods
  }

  showSaveSuccess() {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = "Changes saved successfully"
      this.statusIndicatorTarget.className = "text-sm text-green-600"
      setTimeout(() => {
        if (this.hasStatusIndicatorTarget) {
          this.statusIndicatorTarget.textContent = ""
        }
      }, 3000)
    }
  }

  showSaveError(errors) {
    if (this.hasStatusIndicatorTarget) {
      this.statusIndicatorTarget.textContent = `Save failed: ${errors.join(", ")}`
      this.statusIndicatorTarget.className = "text-sm text-red-600"
    }
  }

  // Utility Methods
  deepClone(obj) {
    return JSON.parse(JSON.stringify(obj))
  }

  debounce(func, wait) {
    let timeout
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout)
        func(...args)
      }
      clearTimeout(timeout)
      timeout = setTimeout(later, wait)
    }
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content")
  }
}
